import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Sparkles, ArrowLeft, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listLiveProducts } from "@/lib/products";
import type { RichProduct } from "@/lib/products";

/* ── Question definitions ─────────────────────────────────── */
interface Question {
  id: string;
  text: string;
  options: { value: string; label: string; emoji?: string }[];
}

const QUESTIONS: Question[] = [
  {
    id: "substrate",
    text: "What are you mainly printing on?",
    options: [
      { value: "garments", label: "Cotton garments & apparel", emoji: "👕" },
      { value: "hard", label: "Hard surfaces & sublimation", emoji: "🖨️" },
      { value: "mixed", label: "Mixed — a bit of everything", emoji: "🎨" },
    ],
  },
  {
    id: "volume",
    text: "How many metres per month (roughly)?",
    options: [
      { value: "low", label: "Under 5 m", emoji: "🌱" },
      { value: "mid", label: "5 – 20 m", emoji: "📦" },
      { value: "high", label: "20+ m", emoji: "🚀" },
    ],
  },
  {
    id: "cutting",
    text: "Do you need cutting, or full sheets only?",
    options: [
      { value: "cut", label: "Cut to shape / individual transfers", emoji: "✂️" },
      { value: "sheet", label: "Full gang sheets (I'll cut myself)", emoji: "📋" },
      { value: "both", label: "Both — it depends on the job", emoji: "🔀" },
    ],
  },
  {
    id: "gangsheet",
    text: "Do you already have finished gang sheet artwork?",
    options: [
      { value: "yes", label: "Yes — I'll upload my own sheet", emoji: "📤" },
      { value: "no", label: "No — I need to build one", emoji: "🛠️" },
      { value: "unsure", label: "Not sure yet", emoji: "🤔" },
    ],
  },
];

/* ── Recommendation engine ────────────────────────────────── */
function getRecommendation(
  answers: Record<string, string>,
  products: RichProduct[],
): { product: RichProduct | null; reason: string; useBuilder: boolean } {
  const wantsGangSheet = answers.gangsheet !== "yes";
  const highVol = answers.volume === "high";
  const wantsCut = answers.cutting === "cut";

  // Prefer gang-sheet products for builder flow; otherwise any live product
  const gangProducts = products.filter((p) => p.is_gangsheet);
  const transferProducts = products.filter((p) => !p.is_gangsheet);

  let product: RichProduct | null = null;
  let reason = "";

  if (wantsCut || !wantsGangSheet) {
    product = transferProducts[0] ?? products[0] ?? null;
    reason = `Suits individual / cut transfers${highVol ? ", optimised for your volume" : ""}.`;
  } else {
    product = gangProducts[0] ?? products[0] ?? null;
    reason = `Gang sheets let you pack multiple designs onto one sheet${highVol ? " — great value at your volume" : ""}.`;
  }

  const useBuilder = wantsGangSheet && !!gangProducts.length;
  return { product, reason, useBuilder };
}

/* ── Component ────────────────────────────────────────────── */
interface FlightDeckAIProps {
  onClose: () => void;
}

export function FlightDeckAI({ onClose }: FlightDeckAIProps) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const { data: products = [] } = useQuery({
    queryKey: ["live-products"],
    queryFn: listLiveProducts,
  });

  const totalSteps = QUESTIONS.length;
  const isResult = step === totalSteps;
  const currentQ = QUESTIONS[step];
  const rec = isResult ? getRecommendation(answers, products) : null;

  const choose = (value: string) => {
    const next = { ...answers, [currentQ.id]: value };
    setAnswers(next);
    setStep((s) => s + 1);
  };

  const handleStart = () => {
    if (!rec?.product) return;
    onClose();
    nav({ to: "/app/customer/shop/$productId", params: { productId: rec.product.id } });
  };

  const handleSeeAll = () => {
    onClose();
    nav({ to: "/app/customer/shop" });
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="fd-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        key="fd-panel"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ type: "spring", damping: 26, stiffness: 320 }}
        className={cn(
          "fixed z-50 flex flex-col overflow-hidden border border-border bg-card shadow-2xl",
          // Mobile: full-width bottom sheet
          "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-[2rem] rounded-b-none",
          // Tablet/desktop: centred dialog
          "sm:inset-0 sm:m-auto sm:h-fit sm:max-h-[90vh] sm:w-full sm:max-w-md sm:rounded-[2rem]",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag affordance on mobile */}
        <div className="mx-auto mt-2 mb-1 h-1.5 w-12 shrink-0 rounded-full bg-white/40 sm:hidden" />
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-border bg-gradient-hero px-4 py-3 text-primary-foreground sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/15 sm:h-8 sm:w-8">
              <Sparkles className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
            </span>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-base font-bold sm:text-lg">FlightDeck AI</span>
              <span className="truncate text-[11px] font-medium text-primary-foreground/75 sm:text-xs">
                Find your perfect product
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/10 text-primary-foreground transition-colors hover:bg-white/25 active:scale-95 sm:h-9 sm:w-9"
          >
            <X className="h-5 w-5 sm:h-4 sm:w-4" />
          </button>
        </div>

        {/* Progress bar */}
        {!isResult && (
          <div className="h-1 bg-muted">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-6">
          <AnimatePresence mode="wait">
            {!isResult ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.18 }}
              >
                <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Question {step + 1} of {totalSteps}
                </p>
                <h2 className="mb-4 text-lg font-bold sm:mb-5 sm:text-xl">{currentQ.text}</h2>
                <div className="space-y-2">
                  {currentQ.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => choose(opt.value)}
                      className="flex w-full items-center gap-3 rounded-2xl border border-border bg-background p-3.5 text-left transition-all hover:border-primary hover:bg-primary/5 active:scale-[0.99] sm:p-4"
                    >
                      {opt.emoji && (
                        <span className="text-xl leading-none sm:text-2xl">{opt.emoji}</span>
                      )}
                      <span className="font-medium text-sm sm:text-base">{opt.label}</span>
                      <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-4 flex items-center gap-2 text-primary">
                  <Zap className="h-5 w-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">
                    Our recommendation
                  </span>
                </div>

                {rec?.product ? (
                  <>
                    {rec.product.image_main_url && (
                      <div className="mb-4 aspect-[16/9] overflow-hidden rounded-2xl sm:aspect-[16/7]">
                        <img
                          src={rec.product.image_main_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <h2 className="text-xl font-bold sm:text-2xl">{rec.product.name}</h2>
                    <p className="mt-1 text-muted-foreground">{rec.reason}</p>
                    {rec.product.short_description && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        {rec.product.short_description}
                      </p>
                    )}
                    <div
                      className={cn(
                        "mt-4 rounded-2xl border p-3 text-sm",
                        rec.useBuilder
                          ? "border-primary/30 bg-primary/5 text-primary"
                          : "border-border bg-muted/40 text-muted-foreground",
                      )}
                    >
                      {rec.useBuilder
                        ? "💡 We'll open the gang sheet builder so you can arrange your designs."
                        : "💡 You can upload a finished sheet or individual artwork."}
                    </div>

                    <div className="mt-5 space-y-2">
                      <Button
                        className="w-full rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                        onClick={handleStart}
                      >
                        Start with {rec.product.name}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full rounded-full"
                        onClick={handleSeeAll}
                      >
                        See all products
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-muted-foreground mb-4">No live products available yet.</p>
                    <Button variant="outline" className="rounded-full" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Back nav */}
        {step > 0 && !isResult && (
          <div className="border-t border-border px-6 py-3">
            <button
              onClick={() => setStep((s) => s - 1)}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
